import { Box, Button, Drawer, Group, Select, Stack, Text, Textarea, TextInput, UnstyledButton } from '@mantine/core';
import dayjs from 'dayjs';
import { ReactElement, ReactNode, useState } from 'react';
import { ORBIT_DISABLED, ORBIT_GRAY, STATUS_BADGE_TONES } from '@/theme/colors';
import {
  correctChanges,
  draftVariable,
  parseRate,
  RateDraftRow,
  RateSeries,
  scheduledFrom,
  updateChanges,
  VariableFee,
  variableComplete,
} from './rateSchedule';

/**
 * The prototype's rates drawer (BAR-3039), shared by the fee and cost
 * schedules: Update rates (new versions from a date) or Correct (the rates in
 * force, fixed in place, with a reason).
 */

export type RateSave = { procedure: string; rate: number } & Partial<VariableFee>;

export type RatesDrawerSave =
  | { mode: 'update'; effectiveFrom: string; rates: RateSave[] }
  | { mode: 'correct'; reason: string; rates: RateSave[] };

// The draft keeps Unlimited as `''`; a Select option needs a value of its own.
const UNLIMITED = 'unlimited';
const BASE_COVERS_OPTIONS = [
  { value: '30', label: '30 min' },
  { value: '45', label: '45 min' },
  { value: '60', label: '60 min' },
  { value: '90', label: '90 min' },
  { value: UNLIMITED, label: 'Unlimited' },
];
const EVERY_OPTIONS = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '60 min' },
];

export interface RatesDrawerProps {
  /** The cost schedule's partner rates (BAR-3047): base covers, increment and every per row. */
  variableFees?: boolean;
  /** Procedures that may be left blank, meaning none (the daily minimum); always offered on Update. */
  optionalProcedures?: string[];
  /** `Rate` by default; `Fee`, `Commission per win`. */
  rateLabel?: string;
  opened: boolean;
  mode: 'update' | 'correct';
  groupName: string;
  series: RateSeries[];
  /** Every procedure the schedule knows, for + Add procedure. */
  procedures: { value: string; label: string }[];
  /** `YYYY-MM-DD`. */
  today: string;
  saving: boolean;
  onClose: () => void;
  onSave: (save: RatesDrawerSave) => void;
  onRemoveScheduled: () => void;
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <Box>
      <Text fz={12} fw={500} c={ORBIT_GRAY} mb={4}>
        {label}
      </Text>
      {children}
    </Box>
  );
}

const asText = (rate: number | undefined | null) =>
  rate === undefined || rate === null || rate === 0 ? '' : rate.toFixed(2);

export function RatesDrawer({
  variableFees = false,
  optionalProcedures = [],
  rateLabel = 'Rate',
  opened,
  mode,
  groupName,
  series,
  procedures,
  today,
  saving,
  onClose,
  onSave,
  onRemoveScheduled,
}: RatesDrawerProps): ReactElement {
  const correct = mode === 'correct';
  const pendingFrom = correct ? null : scheduledFrom(series);
  const tomorrow = dayjs(today).add(1, 'day').format('YYYY-MM-DD');
  const [effectiveFrom, setEffectiveFrom] = useState(pendingFrom ?? tomorrow);
  const [reason, setReason] = useState('');
  const optional = (procedure: string) => optionalProcedures.includes(procedure);
  const timed = (procedure: string) => variableFees && !optional(procedure);
  const draft = (procedure: string, version: RateSeries['current'] | undefined, existing: boolean): RateDraftRow => ({
    procedure,
    rate: asText(version?.rate),
    existing,
    ...(timed(procedure)
      ? {
          baseCovers:
            version?.variable?.baseCoversMinutes === null || version?.variable?.baseCoversMinutes === undefined
              ? ''
              : String(version.variable.baseCoversMinutes),
          increment: asText(version?.variable?.increment),
          every:
            version?.variable?.incrementEveryMinutes === null || version?.variable?.incrementEveryMinutes === undefined
              ? ''
              : String(version.variable.incrementEveryMinutes),
        }
      : {}),
  });
  const [rows, setRows] = useState<RateDraftRow[]>(() => {
    const shown = series
      .filter((entry) => !correct || (entry.current && entry.current.rate > 0))
      .map((entry) => draft(entry.procedure, correct ? entry.current : (entry.scheduled ?? entry.current), true));
    // A daily minimum is always offered on Update, blank when there is none.
    const offered = correct
      ? []
      : optionalProcedures
          .filter((procedure) => !shown.some((row) => row.procedure === procedure))
          .map((procedure) => draft(procedure, undefined, true));
    return [...offered, ...shown];
  });
  const label = (procedure: string) => procedures.find((option) => option.value === procedure)?.label ?? procedure;
  const unused = procedures.filter(
    (option) => !optional(option.value) && !rows.some((row) => row.procedure === option.value)
  );
  const setRow = (index: number, change: Partial<RateDraftRow>) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...change } : row)));

  const rateOf = (row: RateDraftRow): number | null =>
    optional(row.procedure) && !row.rate.trim() ? 0 : parseRate(row.rate);
  const filled = rows.length > 0 && rows.every((row) => rateOf(row) !== null && variableComplete(row));
  const changed = correct
    ? correctChanges(rows, series, rateOf)
    : updateChanges(rows, series, effectiveFrom, pendingFrom, rateOf);
  const ready = filled && changed && (correct ? reason.trim().length > 0 : effectiveFrom > today);

  const save = (): void => {
    const rates = rows
      // An untouched blank daily minimum where there was none sends nothing.
      .filter((row) => !(rateOf(row) === 0 && !series.some((entry) => entry.procedure === row.procedure)))
      .map((row) => ({ procedure: row.procedure, rate: rateOf(row) as number, ...draftVariable(row) }));
    onSave(correct ? { mode, reason: reason.trim(), rates } : { mode, effectiveFrom, rates });
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={440}
      title={correct ? 'Correct rates' : 'Update rates'}
    >
      <Stack gap={12}>
        <Box
          px={12}
          py={10}
          style={{
            border: '1px solid rgba(0, 0, 0, 0.10)',
            borderRadius: 8,
            background: STATUS_BADGE_TONES.neutral.background,
          }}
        >
          <Text fz={13} fw={600}>
            {groupName}
          </Text>
        </Box>
        {!correct && (
          <TextInput
            label="Effective from"
            size="xs"
            type="date"
            min={tomorrow}
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.currentTarget.value)}
          />
        )}
        <Field label={correct ? 'Rates in force' : 'Rates'}>
          <Stack gap={6}>
            <Group gap={8} wrap="nowrap">
              <Text fz={12} c={ORBIT_GRAY} style={{ flex: 1 }}>
                Procedure
              </Text>
              <Text fz={12} c={ORBIT_GRAY} style={{ flex: 1 }}>
                {rateLabel}
              </Text>
              <Box w={18} />
            </Group>
            {rows.map((row, index) => (
              <Stack key={`${row.procedure}-${index}`} gap={4}>
                <Group gap={8} wrap="nowrap">
                  {row.existing ? (
                    <Text fz={13} style={{ flex: 1 }}>
                      {label(row.procedure)}
                    </Text>
                  ) : (
                    <Select
                      size="xs"
                      style={{ flex: 1 }}
                      aria-label="Procedure"
                      data={[{ value: row.procedure, label: label(row.procedure) }, ...unused]}
                      value={row.procedure}
                      allowDeselect={false}
                      onChange={(value) => value && setRow(index, { procedure: value })}
                    />
                  )}
                  <TextInput
                    size="xs"
                    style={{ flex: 1 }}
                    aria-label={`${label(row.procedure)} rate`}
                    inputMode="decimal"
                    placeholder={optional(row.procedure) ? 'None' : '0.00'}
                    value={row.rate}
                    onChange={(event) => setRow(index, { rate: event.currentTarget.value })}
                  />
                  {row.existing ? (
                    <Box w={18} />
                  ) : (
                    <UnstyledButton
                      w={18}
                      title="Remove"
                      aria-label="Remove"
                      c={ORBIT_DISABLED}
                      onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                    >
                      ×
                    </UnstyledButton>
                  )}
                </Group>
                {row.baseCovers !== undefined && (
                  <Group gap={8} wrap="nowrap" pl={12}>
                    <Select
                      size="xs"
                      style={{ flex: 1 }}
                      aria-label={`${label(row.procedure)} base covers`}
                      data={BASE_COVERS_OPTIONS}
                      value={row.baseCovers || UNLIMITED}
                      allowDeselect={false}
                      onChange={(value) => setRow(index, { baseCovers: !value || value === UNLIMITED ? '' : value })}
                    />
                    {row.baseCovers !== '' && (
                      <>
                        <TextInput
                          size="xs"
                          style={{ flex: 1 }}
                          aria-label={`${label(row.procedure)} increment`}
                          inputMode="decimal"
                          placeholder="Increment"
                          value={row.increment ?? ''}
                          onChange={(event) => setRow(index, { increment: event.currentTarget.value })}
                        />
                        <Select
                          size="xs"
                          style={{ flex: 1 }}
                          aria-label={`${label(row.procedure)} every`}
                          placeholder="Every"
                          data={EVERY_OPTIONS}
                          value={row.every || null}
                          onChange={(value) => setRow(index, { every: value ?? '' })}
                        />
                      </>
                    )}
                    <Box w={18} />
                  </Group>
                )}
              </Stack>
            ))}
            {!correct && unused.length > 0 && (
              <UnstyledButton
                fz={12}
                fw={500}
                style={{ alignSelf: 'flex-start', marginTop: 4 }}
                onClick={() =>
                  setRows((current) => [...current, { procedure: unused[0].value, rate: '', existing: false }])
                }
              >
                + Add procedure
              </UnstyledButton>
            )}
          </Stack>
        </Field>
        {correct && (
          <Textarea
            label="Reason"
            size="xs"
            autosize
            minRows={2}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        )}
        {pendingFrom && (
          <UnstyledButton
            fz={12}
            fw={500}
            c={STATUS_BADGE_TONES.error.color}
            style={{ alignSelf: 'flex-start' }}
            onClick={onRemoveScheduled}
          >
            Remove the scheduled update
          </UnstyledButton>
        )}
        <Group justify="flex-end" gap={8} mt={6}>
          <Button size="xs" variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button size="xs" color="dark" loading={saving} disabled={!ready} onClick={save}>
            {correct ? 'Save correction' : 'Save'}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
